import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faToggleOn, faToggleOff, faMobileAlt, faMoneyBillWave, faTags, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { AuthGuard, EmailGuard } from "../App"; // Assuming AuthGuard and RoleGuard exist
import { StandardURL } from "../App"; // Import StandardURL from App.js

// --- Reusable Modal Component ---
const BaseModal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all duration-300 scale-100">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-3xl">&times;</button>
                </div>
                {children}
            </div>
        </div>
    );
};

// --- Modals (Defined outside the main component to prevent focus loss issues) ---

// 1. Add/Edit Category Modal (Handles POST /add and PATCH /category/update)
const AddEditCategoryModal = ({ isOpen, onClose, onSubmit, formData, handleChange, isEdit = false, selectedSetting, loading }) => {
    const isPersonal = formData.smscategory === 'personal';
    const isUpdate = isEdit && selectedSetting;

    const modalTitle = isUpdate ? `Update Category for Property ID: ${selectedSetting.propertyId}` : "Add New SMS Setting";
    const submitLabel = isUpdate ? "Update Category" : "Add Setting";

    return (
        <BaseModal isOpen={isOpen} onClose={onClose} title={modalTitle}>
            <form onSubmit={onSubmit}>
                <div className="p-6 grid grid-cols-1 gap-4">

                    {/* Property ID is only needed when adding a new setting */}
                    {!isUpdate && (
                        <input
                            type="text"
                            name="propertyId"
                            value={formData.propertyId || ''}
                            onChange={handleChange}
                            placeholder="Property ID"
                            className="border border-gray-300 rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                            required
                        />
                    )}
                    
                    {/* Category Selection */}
                    <select
                        name="smscategory"
                        value={formData.smscategory || 'common'}
                        onChange={handleChange}
                        className="border border-gray-300 rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                        required
                    >
                        <option value="common">Common</option>
                        <option value="personal">Personal</option>
                    </select>

                    <input
                        type="text"
                        name="smsphonenumber"
                        value={formData.smsphonenumber || ''}
                        onChange={handleChange}
                        placeholder="SMS Phone Number (e.g., 2547...)"
                        className="border border-gray-300 rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                        required
                    />

                    {/* Conditional fields for Personal category */}
                    {isPersonal && (
                        <>
                            <input
                                type="text"
                                name="inyumbasenderid"
                                value={formData.inyumbasenderid || ''}
                                onChange={handleChange}
                                placeholder="iNyumba Sender ID"
                                className="border border-gray-300 rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                            <input
                                type="text"
                                name="inyumbasmstoken"
                                value={formData.inyumbasmstoken || ''}
                                onChange={handleChange}
                                placeholder="iNyumba SMS Token"
                                className="border border-gray-300 rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                        </>
                    )}
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-xl">
                    <button type="button" onClick={onClose} className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition duration-200">Cancel</button>
                    <button type="submit" disabled={loading} className={`px-5 py-2 rounded-lg text-white font-semibold shadow-md transition duration-200 ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}>
                        {loading ? <FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> : <FontAwesomeIcon icon={isUpdate ? faTags : faPlus} className="mr-2" />}
                        {submitLabel}
                    </button>
                </div>
            </form>
        </BaseModal>
    );
};

// 2. Update Rate Modal
const UpdateRateModal = ({ isOpen, onClose, onSubmit, formData, handleChange, selectedSetting, loading }) => {
    return (
        <BaseModal isOpen={isOpen} onClose={onClose} title={`Update Rate for Property ID: ${selectedSetting?.propertyId}`}>
            <form onSubmit={onSubmit}>
                <div className="p-6">
                    <input
                        type="number"
                        step="0.01"
                        name="smsrate"
                        value={formData.smsrate || ''}
                        onChange={handleChange}
                        placeholder="New SMS Rate (e.g., 5.0)"
                        className="w-full border border-gray-300 rounded-lg p-3 focus:ring-green-500 focus:border-green-500"
                        required
                    />
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end">
                    <button type="button" onClick={onClose} className="mr-3 px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100">Cancel</button>
                    <button type="submit" disabled={loading} className={`px-5 py-2 rounded-lg text-white font-semibold shadow-md ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}>
                        {loading ? <FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> : <FontAwesomeIcon icon={faEdit} className="mr-2" />}
                        Update Rate
                    </button>
                </div>
            </form>
        </BaseModal>
    );
};

// 3. Update Phone Number Modal
const UpdatePhoneModal = ({ isOpen, onClose, onSubmit, formData, handleChange, selectedSetting, loading }) => {
    return (
        <BaseModal isOpen={isOpen} onClose={onClose} title={`Update Phone Number for Property ID: ${selectedSetting?.propertyId}`}>
            <form onSubmit={onSubmit}>
                <div className="p-6">
                    <input
                        type="text"
                        name="phonenumber"
                        value={formData.phonenumber || ''}
                        onChange={handleChange}
                        placeholder="New Phone Number (e.g., 2547...)"
                        className="w-full border border-gray-300 rounded-lg p-3 focus:ring-green-500 focus:border-green-500"
                        required
                    />
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end">
                    <button type="button" onClick={onClose} className="mr-3 px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100">Cancel</button>
                    <button type="submit" disabled={loading} className={`px-5 py-2 rounded-lg text-white font-semibold shadow-md ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}>
                        {loading ? <FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> : <FontAwesomeIcon icon={faMobileAlt} className="mr-2" />}
                        Update Number
                    </button>
                </div>
            </form>
        </BaseModal>
    );
};

// 4. SMS Token Top-Up Modal
const SmsTopUpModal = ({ isOpen, onClose, onSubmit, formData, handleChange, selectedSetting, loading }) => {
    return (
        <BaseModal isOpen={isOpen} onClose={onClose} title={`SMS Token Top-Up for Property ID: ${selectedSetting?.propertyId}`}>
            <form onSubmit={onSubmit}>
                <div className="p-6 grid grid-cols-1 gap-4">
                    <input
                        type="number"
                        name="amount"
                        value={formData.amount || ''}
                        onChange={handleChange}
                        placeholder="Amount to Top Up (Ksh)"
                        className="border border-gray-300 rounded-lg p-3 focus:ring-purple-500 focus:border-purple-500"
                        required
                    />
                    <input
                        type="text"
                        name="phone_number"
                        value={formData.phone_number || ''}
                        onChange={handleChange}
                        placeholder="M-Pesa Phone (e.g., 2547...)"
                        className="border border-gray-300 rounded-lg p-3 focus:ring-purple-500 focus:border-purple-500"
                        required
                    />
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end">
                    <button type="button" onClick={onClose} className="mr-3 px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100">Cancel</button>
                    <button type="submit" disabled={loading} className={`px-5 py-2 rounded-lg text-white font-semibold shadow-md ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-purple-600 hover:bg-purple-700"}`}>
                        {loading ? <FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> : <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2" />}
                        Send STK Push
                    </button>
                </div>
            </form>
        </BaseModal>
    );
};

const SmsIntroductionPage = () => {
    const [settings, setSettings] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingActionId, setLoadingActionId] = useState(null); // Used for specific row actions
    
    // Modals state
    const [isAddEditCategoryModalOpen, setIsAddEditCategoryModalOpen] = useState(false);
    const [isRateModalOpen, setIsRateModalOpen] = useState(false);
    const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
    const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false);

    const [selectedSetting, setSelectedSetting] = useState(null); // The property being acted on
    const [isEditCategoryMode, setIsEditCategoryMode] = useState(false); // Differentiates Add vs Category Update

    // Form data state (used by all modals)
    const initialFormState = {
        propertyId: '', // Only used for Add
        smsrate: '',
        phonenumber: '',
        smscategory: 'common',
        smsphonenumber: '',
        inyumbasenderid: '',
        inyumbasmstoken: '',
        amount: '',
        phone_number: '',
    };
    const [formData, setFormData] = useState(initialFormState);

    const token = localStorage.getItem("token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
    
    useEffect(() => {
        fetchSettings();
    }, []);

    // Helper for form changes
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const resetFormAndSelection = () => {
        setFormData(initialFormState);
        setSelectedSetting(null);
    };

    // --- API Calls ---

    // 1. FETCH ALL SETTINGS (GET: /iNyumba/admin/sms/settings/viewall)
    const fetchSettings = async () => {
        if (!token) {
            toast.error("Authentication token not found.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.get(
                `${StandardURL}/iNyumba/admin/sms/settings/viewall`,
                config
            );
            
            setSettings(Array.isArray(response.data.response) ? response.data.response : []);
            toast.success("SMS settings fetched successfully!");
        } catch (error) {
            console.error("Error fetching SMS settings:", error);
            toast.error(error.response?.data?.response || "Failed to fetch SMS settings.");
            setSettings([]);
        } finally {
            setIsLoading(false);
        }
    };

    // 2. TOGGLE STATUS (PATCH: /iNyumba/admin/sms/settings/status/{propertyId})
    const handleToggleStatus = async (setting) => {
        const propertyId = setting.propertyId;
        if (!token || !propertyId) return;

        const newStatus = setting.status === true ? "OFF" : "ON";
        const isConfirmed = window.confirm(
            `Are you sure you want to toggle status to '${newStatus}' for Property ID: ${propertyId}?`
        );

        if (!isConfirmed) return;

        setLoadingActionId(propertyId);

        try {
            await axios.patch(
                `${StandardURL}/iNyumba/admin/sms/settings/status/${propertyId}`,
                {},
                config
            );
            toast.success(`Status for ${propertyId} toggled to ${newStatus}!`);
            fetchSettings(); // Refresh the list
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error(error.response?.data?.response || "Failed to update status.");
        } finally {
            setLoadingActionId(null);
        }
    };

    // 3. UPDATE RATE (PATCH: /iNyumba/admin/sms/settings/rate/update/{propertyId})
    const handleUpdateRate = async (e) => {
        e.preventDefault();
        const propertyId = selectedSetting?.propertyId;
        if (!token || !propertyId) return;
        
        setLoadingActionId(propertyId);

        try {
            const payload = {
                smsrate: Number(formData.smsrate),
            };

            await axios.patch(
                `${StandardURL}/iNyumba/admin/sms/settings/rate/update/${propertyId}`,
                payload,
                config
            );
            toast.success(`SMS rate for ${propertyId} updated successfully!`);
            setIsRateModalOpen(false);
            resetFormAndSelection();
            fetchSettings();
        } catch (error) {
            console.error("Error updating SMS rate:", error);
            toast.error(error.response?.data?.response || "Failed to update SMS rate.");
        } finally {
            setLoadingActionId(null);
        }
    };

    // 4. UPDATE PHONE NUMBER (PATCH: /iNyumba/admin/sms/settings/phonenumber/update/{propertyId})
    const handleUpdatePhoneNumber = async (e) => {
        e.preventDefault();
        const propertyId = selectedSetting?.propertyId;
        if (!token || !propertyId) return;
        
        setLoadingActionId(propertyId);

        try {
            const payload = {
                phonenumber: formData.phonenumber,
            };

            await axios.patch(
                `${StandardURL}/iNyumba/admin/sms/settings/phonenumber/update/${propertyId}`,
                payload,
                config
            );
            toast.success(`Phone number for ${propertyId} updated successfully!`);
            setIsPhoneModalOpen(false);
            resetFormAndSelection();
            fetchSettings();
        } catch (error) {
            console.error("Error updating phone number:", error);
            toast.error(error.response?.data?.response || "Failed to update phone number.");
        } finally {
            setLoadingActionId(null);
        }
    };
    
    // 5 & 6. ADD SETTING (POST: /iNyumba/admin/sms/settings/add/{propertyId}) / 
    //        UPDATE CATEGORY (PATCH: /iNyumba/admin/sms/settings/category/update/{propertyId})
    const handleAddOrUpdateCategory = async (e) => {
        e.preventDefault();
        
        const propertyId = isEditCategoryMode ? selectedSetting?.propertyId : formData.propertyId;
        if (!token || !propertyId) return;

        setLoadingActionId(propertyId);
        
        const isPersonal = formData.smscategory === 'personal';
        let payload = {
            smscategory: formData.smscategory,
            smsphonenumber: formData.smsphonenumber,
        };

        if (isPersonal) {
            payload = {
                ...payload,
                inyumbasenderid: formData.inyumbasenderid,
                inyumbasmstoken: formData.inyumbasmstoken,
            };
        }
        
        try {
            let url = '';
            let method = '';
            let successMessage = '';
            
            if (isEditCategoryMode) {
                url = `${StandardURL}/iNyumba/admin/sms/settings/category/update/${propertyId}`;
                method = 'patch';
                successMessage = `Category for ${propertyId} updated to ${formData.smscategory.toUpperCase()} successfully!`;
            } else {
                url = `${StandardURL}/iNyumba/admin/sms/settings/add/${propertyId}`;
                method = 'post';
                successMessage = `New SMS setting added for Property ID: ${propertyId}!`;
            }

            if (method === 'post') {
                await axios.post(url, payload, config);
            } else if (method === 'patch') {
                await axios.patch(url, payload, config);
            }

            toast.success(successMessage);
            setIsAddEditCategoryModalOpen(false);
            setIsEditCategoryMode(false);
            resetFormAndSelection();
            fetchSettings();
        } catch (error) {
            console.error(`Error ${isEditCategoryMode ? 'updating category' : 'adding setting'}:`, error);
            toast.error(error.response?.data?.response || `Failed to ${isEditCategoryMode ? 'update category' : 'add setting'}.`);
        } finally {
            setLoadingActionId(null);
        }
    };

    // 7. SMS TOKEN TOP UP (POST: /iNyumba/stkpushsmstopup/{propertyId})
    const handleSmsTopUp = async (e) => {
        e.preventDefault();
        const propertyId = selectedSetting?.propertyId;
        if (!token || !propertyId) return;

        setLoadingActionId(propertyId);

        try {
            const payload = {
                amount: Number(formData.amount),
                phone_number: formData.phone_number,
            };

            await axios.post(
                `${StandardURL}/iNyumba/stkpushsmstopup/${propertyId}`,
                payload,
                config
            );
            toast.success(`STK Push initiated for ${propertyId}. Check your phone to complete payment!`);
            setIsTopUpModalOpen(false);
            resetFormAndSelection();
            fetchSettings();
        } catch (error) {
            console.error("Error initiating top-up:", error);
            toast.error(error.response?.data?.response || "Failed to initiate top-up.");
        } finally {
            setLoadingActionId(null);
        }
    };

    // --- Open Modal Functions ---
    const openModal = (setter, setting = null, mode = 'view') => {
        setSelectedSetting(setting);
        
        let newFormData = { ...initialFormState };
        if (setting) {
            // Pre-populate fields for modals that need current data
            if (setter === setIsRateModalOpen) {
                newFormData.smsrate = setting.smsrate;
            } else if (setter === setIsPhoneModalOpen) {
                newFormData.phonenumber = setting.phonenumber;
            } else if (setter === setIsAddEditCategoryModalOpen) {
                setIsEditCategoryMode(true);
                newFormData.smscategory = setting.smscategory || 'common';
                newFormData.smsphonenumber = setting.smsphonenumber || '';
                newFormData.inyumbasenderid = setting.inyumbasenderid || '';
                newFormData.inyumbasmstoken = setting.inyumbasmstoken || '';
            }
        } else {
             // For Add setting mode
            setIsEditCategoryMode(false);
        }
        setFormData(newFormData);
        setter(true);
    };

    return (
        <EmailGuard>
        <AuthGuard>
            <div className="container mx-auto px-4 py-8">                
                <h1 className="text-4xl font-extrabold text-gray-900 mb-8 border-b-4 border-blue-600 pb-2">
                    SMS Settings Management
                </h1>

                {/* Header and Add Button */}
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-gray-700">All SMS Settings</h2>
                    <button
                        onClick={() => openModal(setIsAddEditCategoryModalOpen)}
                        className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition duration-300 transform hover:scale-[1.02]"
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Add New Setting
                    </button>
                </div>

                {/* Settings Table */}
                <div className="bg-white shadow-2xl rounded-xl overflow-hidden">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-12">
                            <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-blue-600" />
                            <span className="ml-3 text-lg text-gray-600">Loading SMS settings...</span>
                        </div>
                    ) : settings.length === 0 ? (
                        <div className="text-center py-10 text-gray-500 text-lg">
                            No SMS settings found.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {["Property ID", "Category", "Rate (KSh)", "Phone Number", "Sender ID", "Token Balance", "Status","Payment Status", "Actions"].map((header) => (
                                            <th
                                                key={header}
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {settings.map((setting) => (
                                        <tr key={setting.propertyId} className="hover:bg-gray-50 transition duration-150">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{setting.propertyId}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">{setting.smscategory || 'N/A'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{Number(setting.smsrate || 0).toFixed(2)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{setting.propertyphonenumber || 'N/A'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{setting.smssenderid || 'N/A'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">{Number(setting.smsbalance || 0).toLocaleString()}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{setting.status || 'N/A'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{setting.paymentstatus || 'N/A'}</td>

                                            <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                                                <div className="flex space-x-2">
                                                    {/* Toggle Status (PATCH) */}
                                                    <button
                                                        onClick={() => handleToggleStatus(setting)}
                                                        disabled={loadingActionId === setting.propertyId}
                                                        className={`p-2 rounded-full transition duration-150 disabled:opacity-50 ${
                                                            setting.status 
                                                                ? 'text-red-600 hover:text-red-900 hover:bg-red-50'
                                                                : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                                                        }`}
                                                        title={setting.status ? 'Deactivate' : 'Activate'}
                                                    >
                                                        {loadingActionId === setting.propertyId ? <FontAwesomeIcon icon={faSpinner} spin /> : (
                                                            <FontAwesomeIcon icon={setting.status ? faToggleOn : faToggleOff} />
                                                        )}
                                                    </button>
                                                    
                                                    {/* Update Rate (PATCH) */}
                                                    <button
                                                        onClick={() => openModal(setIsRateModalOpen, setting)}
                                                        className="text-indigo-600 hover:text-indigo-900 p-2 rounded-full hover:bg-indigo-50 transition duration-150"
                                                        title="Update SMS Rate"
                                                    >
                                                        <FontAwesomeIcon icon={faMoneyBillWave} />
                                                    </button>

                                                    {/* Update Phone Number (PATCH) */}
                                                    <button
                                                        onClick={() => openModal(setIsPhoneModalOpen, setting)}
                                                        className="text-purple-600 hover:text-purple-900 p-2 rounded-full hover:bg-purple-50 transition duration-150"
                                                        title="Update Phone Number"
                                                    >
                                                        <FontAwesomeIcon icon={faMobileAlt} />
                                                    </button>
                                                    
                                                    {/* Update Category (PATCH) */}
                                                    <button
                                                        onClick={() => openModal(setIsAddEditCategoryModalOpen, setting, 'edit')}
                                                        className="text-orange-600 hover:text-orange-900 p-2 rounded-full hover:bg-orange-50 transition duration-150"
                                                        title="Update Category"
                                                    >
                                                        <FontAwesomeIcon icon={faTags} />
                                                    </button>

                                                    {/* SMS Token Top Up (POST) */}
                                                    <button
                                                        onClick={() => openModal(setIsTopUpModalOpen, setting)}
                                                        className="text-cyan-600 hover:text-cyan-900 p-2 rounded-full hover:bg-cyan-50 transition duration-150"
                                                        title="SMS Token Top Up"
                                                    >
                                                        <FontAwesomeIcon icon={faPlus} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals Section */}

            {/* Add/Edit Category Modal */}
            <AddEditCategoryModal
                isOpen={isAddEditCategoryModalOpen}
                onClose={() => {setIsAddEditCategoryModalOpen(false); setIsEditCategoryMode(false); resetFormAndSelection();}}
                onSubmit={handleAddOrUpdateCategory}
                formData={formData}
                handleChange={handleChange}
                isEdit={isEditCategoryMode}
                selectedSetting={selectedSetting}
                loading={loadingActionId}
            />

            {/* Update Rate Modal */}
            <UpdateRateModal
                isOpen={isRateModalOpen}
                onClose={() => {setIsRateModalOpen(false); resetFormAndSelection();}}
                onSubmit={handleUpdateRate}
                formData={formData}
                handleChange={handleChange}
                selectedSetting={selectedSetting}
                loading={loadingActionId}
            />
            
            {/* Update Phone Number Modal */}
            <UpdatePhoneModal
                isOpen={isPhoneModalOpen}
                onClose={() => {setIsPhoneModalOpen(false); resetFormAndSelection();}}
                onSubmit={handleUpdatePhoneNumber}
                formData={formData}
                handleChange={handleChange}
                selectedSetting={selectedSetting}
                loading={loadingActionId}
            />
            
            {/* SMS Token Top Up Modal */}
            <SmsTopUpModal
                isOpen={isTopUpModalOpen}
                onClose={() => {setIsTopUpModalOpen(false); resetFormAndSelection();}}
                onSubmit={handleSmsTopUp}
                formData={formData}
                handleChange={handleChange}
                selectedSetting={selectedSetting}
                loading={loadingActionId}
            />

        </AuthGuard>
   </EmailGuard>
    );
};

export default SmsIntroductionPage;